import { useEffect, useState, type JSX } from "react";
import { Moon, Sun } from "lucide-react";

/** `THEME_STORAGE_KEY` (index.html:5332). */
const THEME_SPEICHER_SCHLUESSEL = "hubTheme";

/**
 * `.brand-theme-panel` (index.html:3641-3647) samt Umschaltverhalten (`toggleDarkMode`/
 * `applyTheme`/`updateThemeUi`, index.html:5334-5363).
 *
 * Liest den Anfangszustand aus `document.documentElement.dataset.theme` statt erneut aus
 * `localStorage`: `App.tsx` setzt dieses Attribut bereits beim Start (Entsprechung zu `initTheme()`,
 * index.html:14936 - unbedingt, unabhängig vom Login-Status, damit auch der Anmeldebildschirm das
 * gespeicherte Theme zeigt). Für den Wechsel selbst bleibt dieses Modul die einzige Quelle: es
 * schreibt sowohl das Attribut als auch `localStorage` direkt, ohne Umweg über `Kopfzeile`/
 * `MarkenMenue`-Props - im Original ist das Theme ebenfalls kein React-artig gehobener Zustand,
 * sondern global über DOM-Attribut und `localStorage` geführt.
 */
export function ThemeSchalter(): JSX.Element {
  const [dunkel, setDunkel] = useState(() => document.documentElement.dataset.theme === "dark");

  useEffect(() => {
    document.documentElement.dataset.theme = dunkel ? "dark" : "light";
  }, [dunkel]);

  function umschalten(event: React.MouseEvent): void {
    event.stopPropagation();
    setDunkel((vorher) => {
      const neu = !vorher;
      try {
        localStorage.setItem(THEME_SPEICHER_SCHLUESSEL, neu ? "dark" : "light");
      } catch (_) { // still: wie im Original (applyTheme, index.html:5346-5352) darf ein blockiertes localStorage (privates Fenster o. ae.) den Umschalter nicht scheitern lassen
      }
      return neu;
    });
  }

  return (
    <div className="brand-theme-panel">
      <button className="brand-theme-toggle" type="button" onClick={umschalten}>
        <span className="brand-theme-glyph" id="brandThemeGlyph">
          {dunkel ? <Sun aria-hidden="true" strokeWidth={1.8} /> : <Moon aria-hidden="true" strokeWidth={1.8} />}
        </span>
        <span className="brand-theme-copy">
          <strong id="brandThemeLabel">{dunkel ? "Light Mode" : "Dark Mode"}</strong>
          <small id="brandThemeHint">{dunkel ? "Helle Darstellung aktivieren" : "Dunkle Darstellung aktivieren"}</small>
        </span>
        <span className={dunkel ? "theme-switch on" : "theme-switch"} id="brandThemeSwitch" aria-hidden="true" />
      </button>
    </div>
  );
}
